import algoliasearch from 'algoliasearch';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';

const LOCALIZED_ATTRIBUTES = ['area_name', 'city_name', 'country_name', 'state_name', 'name', 'description'];
const TIER_1_FAILBACK_LANGUAGE_REGION_CODES = ['de-DE', 'en-US', 'es-ES', 'fr-FR'];

class Search {
  constructor(indexId, languageRegionCode) {
    this.credentials = getApplicationConfigForIndex(indexId);
    this.algoliaClient = algoliasearch(this.credentials.applicationId, this.credentials.apiKey);
    this.algoliaIndex = this.algoliaClient.initIndex(indexId);
    this.languageRegionCode = languageRegionCode || LanguageRegionCode.English;
    this.indexId = indexId;
  }

  search(searchTerm, queryObj) {
    return this.algoliaIndex.search(searchTerm, this.optimizeAndLocalizeQuery(searchTerm, queryObj));
  }

  batchSearch(queryObjs) {
    const queries = queryObjs.map(q => {
      return {
        indexName: this.algoliaIndex.indexName,
        query: '',
        params: this.optimizeAndLocalizeQuery('', q)
      };
    });

    return this.algoliaClient.multipleQueries(queries);
  }

  clearCache() {
    this.algoliaClient.clearCache();
  }

  optimizeQuery(searchTerm, query) {
    if (!searchTerm) {
      query.attributesToHighlight = [];
    } else if (!query.attributesToHighlight) {
      query.attributesToHighlight = ['name'];
    }

    if (!query.responseFields) {
      query.responseFields = ['hits', 'hitsPerPage', 'nbHits'];
    }
  }

  localizeQuery(query) {
    const legacy_suffix = TIER_1_FAILBACK_LANGUAGE_REGION_CODES.includes(this.languageRegionCode) ? this.languageRegionCode.substring(0, 2) : null;
    if (query.attributesToRetrieve) {
      LOCALIZED_ATTRIBUTES.forEach(attr => {
        if (query.attributesToRetrieve.includes(attr)) {
          query.attributesToRetrieve.push(`${attr}_${this.languageRegionCode}`);
          if (legacy_suffix) {
            query.attributesToRetrieve.push(`${attr}_${legacy_suffix}`);
          }
        }
      });
    }
    if (query.attributesToHighlight) {
      query.attributesToHighlight.forEach(attr => {
        query.attributesToHighlight.push(`${attr}_${this.languageRegionCode}`);
        if (legacy_suffix) {
          query.attributesToHighlight.push(`${attr}_${legacy_suffix}`);
        }
      });
    }
  }

  optimizeAndLocalizeQuery(searchTerm, query) {
    if (!query.attributesToRetrieve) {
      console.debug('WARNING: No attributesToRetrieve specified');
      console.debug({ searchTerm, query });
    }

    const optimizedQuery = JSON.parse(JSON.stringify(query)); // deep clone

    this.optimizeQuery(searchTerm, optimizedQuery);
    this.localizeQuery(optimizedQuery);

    return optimizedQuery;
  }
}

const indices = [];

const getAlgoliaIndex = (indexId = null, providedLanguageRegionCode = null) => {
  let algoliaIndex = (indexId) ? indexId : getAlgoliaIndexId('primaryIndex');
  let languageRegionCode = providedLanguageRegionCode;

  if (!languageRegionCode) {
    languageRegionCode = LanguageRegionCode.English;
  }

  const cacheKey = [algoliaIndex, languageRegionCode].join('-');
  indices[cacheKey] ||= new Search(algoliaIndex, languageRegionCode);
  return indices[cacheKey];
};

const getAlgoliaConfig = () => {
  return global.__AT_DATA__.algoliaConfig;
}

const getAlgoliaIndexId = (key) => {
  return getAlgoliaConfig().indexes[key]?.id;
}

const getApplicationConfigForIndex = (indexId) => {
  const index = Object.values(getAlgoliaConfig().indexes).find((i) => i.id == indexId);
  if (index) {
    return getAlgoliaConfig().applications[index.application];
  } else {
    return {};
  }
}

const getPrimaryIndex = (languageRegionCode = null) => {
  // TODO: Remove languageRegionCode after language-specific indexes launch, will not be relevant
  return getAlgoliaIndex(getAlgoliaIndexId('primaryIndex'), languageRegionCode);
};

const getPendingIndex = () => {
  return getAlgoliaIndex(getAlgoliaIndexId('pendingIndex'));
}

const getMapsIndex = () => {
  return getAlgoliaIndex(getAlgoliaIndexId('mapsIndex'));
}

export { Search, getAlgoliaIndex, getPrimaryIndex, getPendingIndex, getMapsIndex, getApplicationConfigForIndex };
